import { FAQSectionContent, ReviewData } from '../content';

/**
 * Build FAQ schema for SGP pages
 * @returns {object|null} FAQ schema object or null if no FAQs
 */
export const buildMainFaqSchema = () => {
	const faqs = FAQSectionContent?.faqs || [];

	if (faqs.length === 0) {
		return null;
	}

	const faqSchema = {
		'@type': 'FAQPage',
		mainEntity: faqs.map((faq) => ({
			'@type': 'Question',
			name: faq.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: faq.answer,
			},
		})),
	};

	return faqSchema;
};

/**
 * Build Review schema for SGP pages
 * @returns {object|null} Review schema object or null if no reviews
 */
export const buildMainReviewSchema = () => {
	const reviews = ReviewData?.reviews || [];

	if (reviews.length === 0) {
		return undefined;
	}

	return reviews.map((review) => ({
		'@type': 'Review',
		author: {
			'@type': 'Person',
			name: review.customerName || '',
		},
		datePublished: review.date || '',
		reviewRating: {
			'@type': 'Rating',
			ratingValue: review.stars?.toString() || '5',
			bestRating: '5',
			worstRating: '1',
		},
		reviewBody: review.text || '',
		url:
			review.url ||
			'https://www.trustpilot.com/review/www.selfstorage.com',
	}));
};

/**
 * Build JSON-LD schema for the Size Guide page.
 * Returns a combined @graph schema with WebPage, WebSite,
 * BreadcrumbList, and Guide types per SEO spec Section 7.6.1.
 *
 * @param {object} metadata - Page metadata
 * @param {string} metadata.title - Page title
 * @param {string} metadata.description - Meta description
 * @param {string} metadata.canonical - Canonical URL (includes base URL from store)
 * @returns {object} JSON-LD schema object
 */
export const buildMainSizeGuideSchema = (metadata) => {
	const reviews = buildMainReviewSchema();

	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'WebPage',
				url: 'https://www.selfstorage.com/self-storage-sizes/',
				name: 'Self Storage Size Guide',
				description: metadata.description,
				inLanguage: 'en-US',
				isPartOf: {
					'@type': 'WebSite',
					name: 'http://SelfStorage.com',
					url: 'https://www.selfstorage.com/',
				},
			},
			{
				'@type': 'WebSite',
				name: 'http://SelfStorage.com',
				url: 'https://www.selfstorage.com/',
			},
			{
				'@type': 'BreadcrumbList',
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						name: 'Home',
						item: 'https://www.selfstorage.com/',
					},
					{
						'@type': 'ListItem',
						position: 2,
						name: 'Self Storage Size Guide',
						item: 'https://www.selfstorage.com/self-storage-sizes/',
					},
				],
			},
			{
				'@type': 'Guide',
				url: 'https://www.selfstorage.com/self-storage-sizes/',
				name: 'Self Storage Size Guide',
				description: metadata.description,
				inLanguage: 'en-US',
				about: {
					'@type': 'Thing',
					name: 'Self Storage Units',
				},
				isPartOf: {
					'@type': 'WebPage',
					url: 'https://www.selfstorage.com/self-storage-sizes/',
				},
			},
			{
				'@type': 'Organization',
				name: 'SelfStorage.com',
				url: 'https://www.selfstorage.com/',
				aggregateRating: {
					'@type': 'AggregateRating',
					ratingValue: '4.4',
					reviewCount: '77492',
					bestRating: '5',
					worstRating: '1',
				},
				...(reviews && { review: reviews }),
			},
			buildMainFaqSchema(),
		].filter(Boolean),
	};
};
